/**
 * Authentication library.
 *
 * provides helpers on things like getting access to the token, checking
 * if user is authenticated and other kind of access / auth functionality.
 */

import { User } from '~/jobiqo/models/user';
import GET_USER_INFO from '~/jobiqo/graphql/queries/getUser.gql';
import redirect from './redirect';
import { Message } from '~/jobiqo/models/app';
import { Resume } from '~/jobiqo/types/types';
import { ApolloClient } from '@apollo/client';
import initApollo from '../apollo/initApollo';
import { getSession } from 'next-auth/react';
import { encode } from 'next-auth/jwt';
import { setServerCookie } from './session';

export const ROLE_RECRUITER = 'recruiter';
export const ROLE_APPLICANT = 'applicant';
export const ROLE_ADMIN = 'administrator';
export const ROLE_SITE_ADMIN = 'site_admin';
export const ROLE_JOB_EDITOR = 'job_editor';
export const ANONYMOUS_USER = 'anonymous';

/**
 * Checks if a token exits and is set.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the token exists and false otherwise.
 */
export const checkToken = async (ctx: any): Promise<boolean> => {
  const session = await getSession(ctx);
  const token = session?.jwt;
  return !!token;
};

/**
 * Gets the token from the context object.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {string} token
 *  Returns the token.
 */
export const getToken = async (ctx: any): Promise<string> => {
  const session = await getSession(ctx);
  const token = session?.jwt as string;
  return token;
};

/**
 * Checks if a user is authenticated.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is authenticated and false otherwise.
 */
export const isAuthenticated = async (ctx: any): Promise<boolean> => {
  return !isAnonymousByHeaders(ctx.req);
};

/**
 * Redirects a user to the user page if he is authenticated.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is was redirected and false otherwise.
 */
export const redirectIfAuthenticated = async (ctx: any): Promise<boolean> => {
  if (await isAuthenticated(ctx)) {
    redirect('/user', ctx);
    return true;
  }
  return false;
};

/**
 * Redirects a user to the login page if he is not authenticated.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is was redirected and false otherwise.
 */
export const redirectIfNotAuthenticated = async (
  ctx: any,
): Promise<boolean> => {
  if (!(await isAuthenticated(ctx))) {
    redirect('/user/login', ctx);
    return true;
  }
  return false;
};

/**
 * Checks if a user is anonymous based on headers.
 *
 * @param {any} req
 *   The current request.
 */
export const isAnonymousByHeaders = (req: any) => {
  return (
    !req?.cookies['next-auth.session-token'] &&
    !req?.cookies['__Secure-next-auth.session-token']
  );
};

/**
 * Logout a user by redirecting to /user/logout.
 *
 * @param {NextApiRequest} req
 *    The request object from nextjs data methods.
 * @param {NextApiResponse} res
 *    The response object from nextjs data methods.
 * @param {boolean} redirectUser
 *    If true, user wil be redirected to the root page
 */
export const logoutServer = async (ctx, redirectUser = true) => {
  // NextAuth SignOut method only runs client side
  // therefore we set the header to delete the cookie here.
  // NextAuth has different cookie names for staging and production.

  // Development cookies.
  if (ctx.req.cookies['next-auth.session-token']) {
    await ctx.res.setHeader(
      'Set-Cookie',
      'next-auth.session-token=deleted; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;',
    );
  }

  // Production cookies.
  if (ctx.req.cookies['__Secure-next-auth.session-token']) {
    await ctx.res.setHeader(
      'Set-Cookie',
      '__Secure-next-auth.session-token=deleted; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure;',
    );
  }
  if (ctx.req.cookies['__Host-next-auth.csrf-token']) {
    await ctx.res.setHeader(
      'Set-Cookie',
      '__Host-next-auth.csrf-token=deleted; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure;',
    );
  }

  if (redirectUser) {
    redirect(`/`, ctx);
  }
};

/**
 * Sign in a user and set the server cookie.
 *
 * @param res
 *  The response object from nextjs data methods.
 * @param jwt
 *  The jwt token to sign.
 * @param masquerade
 *  If true, the user will be masqueraded.
 */
export async function signInAndSetServerCookie(
  res: any,
  jwt: string,
  masquerade = false,
) {
  // Sign and encode token.
  const newEncodedJwt = await encode({
    token: {
      jwt: jwt,
      masqueraded: masquerade,
    },
    secret: process.env.JWT_SECRET as string,
  });
  // NextAuth picks the cookie name from NEXTAUTH_URL: it uses the `__Secure-`
  // prefix when served over HTTPS. We must match that exactly so that
  // next-auth can read the cookie back in `getSession()`; otherwise the
  // session decode silently returns null (e.g. password reset / autologin
  // lands the user on a page rendered as anonymous).
  const useSecureCookies = (process.env.NEXTAUTH_URL ?? '').startsWith(
    'https://',
  );
  const tokenName = useSecureCookies
    ? '__Secure-next-auth.session-token'
    : 'next-auth.session-token';
  setServerCookie(tokenName, newEncodedJwt, res);
}

/**
 * Redirects a user to the home page if he is not a recruiter.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is was redirected and false otherwise.
 */
export const redirectIfNotRecruiter = async (
  ctx,
  path: string = '/403',
  message: Message | null = null,
) => {
  const user = await getUser(ctx);
  if (!isAdmin(user) && !isRecruiter(user)) {
    redirect(path, ctx, message);
    return true;
  }
  return false;
};

/**
 * Redirects a user to the home page if he is not applicant.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is redirected and false otherwise.
 */
export const redirectIfApplicant = async (
  ctx,
  path: string = '/403',
  message: Message | null = null,
) => {
  const user = await getUser(ctx);
  if (isApplicant(user)) {
    redirect(path, ctx, message);
    return true;
  }
  return false;
};

/**
 * Redirects a user to the home page if he is not an applicant.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @return {boolean}
 *  Returns true if the user is was redirected and false otherwise.
 */
export const redirectIfNotApplicant = async (
  ctx,
  path: string = '/403',
  message: Message | null = null,
) => {
  const user = await getUser(ctx);
  if (!isAdmin(user) && !isApplicant(user)) {
    redirect(path, ctx, message);
    return true;
  }
  return false;
};

/**
 * Gets the user's role.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 */
export const getUser = async (ctx): Promise<User> => {
  const apolloClient = initApollo({}, ctx);
  const { data } = await apolloClient.query({
    query: GET_USER_INFO,
  });
  return data.user;
};

/**
 * Check if the user is an administrator or content admin.
 *
 * @param {User} user
 *   The user to check for.
 *
 * @return {boolean}
 *   Return true if the user is an admin, false otherwise.
 */
export const isAdmin = (user: User): boolean => {
  return (
    user &&
    user.roles &&
    (user.roles.map((role) => role.targetId).includes(ROLE_ADMIN) ||
      user.roles.map((role) => role.targetId).includes(ROLE_SITE_ADMIN) ||
      user.roles.map((role) => role.targetId).includes(ROLE_JOB_EDITOR))
  );
};

/**
 * Check if the user is an applicant.
 *
 * @param {User} user
 *   The user to check for.
 *
 * @return {boolean}
 *   Return true if the user is an applicant, false otherwise.
 */
export const isApplicant = (user: User): boolean => {
  return (
    user &&
    user.roles &&
    user.roles.map((role) => role.targetId).includes(ROLE_APPLICANT)
  );
};

/**
 * Check if the user is a recruiter.
 *
 * @param {User} user
 *   The user to check for.
 *
 * @return {boolean}
 *   Return true if the user is a recruiter, false otherwise.
 */
export const isRecruiter = (user: User): boolean => {
  return (
    user &&
    user.roles &&
    user.roles.map((role) => role.targetId).includes(ROLE_RECRUITER)
  );
};

/**
 * Check if the user is an anonymous user.
 *
 * @param {User} user
 *   The user to check for.
 *
 * @return {boolean}
 *   Return true if the user is an admin, false otherwise.
 */
export const isAnonymous = (user: User): boolean => {
  return user === null;
};

/**
 * Check if the user can edit a resume.
 *
 * @param {Resume} resume
 *   The resume to check for.
 *
 * @param {User} user
 *   The user to check for.
 *
 * @return {boolean}
 *   Return true if the user is an admin, false otherwise.
 */
export const canEditResume = (resume: Resume, user: User): boolean => {
  return user.id === resume?.user?.id || isAdmin(user);
};

/**
 * Gets the user's role client side.
 *
 * @param apolloClient
 *  The initialized apollo client in the app.
 */
export const getUserClient = async (
  apolloClient: ApolloClient<any>,
): Promise<User> => {
  const { data } = await apolloClient.query({
    query: GET_USER_INFO,
  });
  return data.user;
};

export interface IUserSession {
  isAuthenticated: boolean;
  user: User | null;
  masqueraded: boolean;
}
